import type { IssueGroupBy } from "./groupAndSort";
import type { IssueStatus } from "@/domains/pm/types";

/**
 * What a new issue created from a Gantt group band should start as.
 *
 * The Kanban's add button maps a column straight onto a status. The Gantt's
 * bands are whatever the user is grouping by, so the mapping has to be read
 * back out of the bucket key that `groupAndSortIssues` assigned:
 *
 *   status      the key IS the status
 *   discipline  `d-<id>`
 *   assignee    `u-<id>`
 *   __none__    the "no discipline" / "no assignee" band — prefill nothing
 *
 * Date buckets prefill nothing on purpose: "This week" is a derived range, not
 * a value an issue can be given, and guessing a date the user did not ask for
 * is worse than opening an empty form.
 */
export interface GanttGroupDefaults {
  status?: IssueStatus;
  assigneeIds?: number[];
  disciplineIds?: number[];
}

export function defaultsForGroup(groupBy: IssueGroupBy, groupKey: string): GanttGroupDefaults {
  if (groupKey === "__none__") return {};
  switch (groupBy) {
    case "status":
      return { status: groupKey };
    case "discipline": {
      const id = idFromKey(groupKey, "d-");
      return id == null ? {} : { disciplineIds: [id] };
    }
    case "assignee": {
      const id = idFromKey(groupKey, "u-");
      return id == null ? {} : { assigneeIds: [id] };
    }
    default:
      return {};
  }
}

function idFromKey(key: string, prefix: string): number | null {
  if (!key.startsWith(prefix)) return null;
  const id = Number(key.slice(prefix.length));
  return Number.isFinite(id) ? id : null;
}
